import type { RootState } from '@app/types';

import type { Language } from '../../../../types/language';
import type { LanguageListItem } from '../../components/Languages';

export const getDownload = (state: RootState) => state.downloadData;

export const getFileFormats = (state: RootState) => state.downloadData?.fileFormats ?? [];

const prepareLanguage = ({ id, ccIso, langIso, name, stats }: Language): LanguageListItem => ({
    id,
    ccIso,
    langIso,
    name,
    progress: stats?.percentTranslatedTotal ?? 0,
});

export const getLanguageListBaseLanguage = (state: RootState): LanguageListItem | undefined => {
    if (!state.downloadData.project?.langs) {
        return undefined;
    }

    const language = state.downloadData.project.langs.find(({ isDefault }) => isDefault);

    if (!language) {
        return undefined;
    }

    return prepareLanguage(language);
};

export const getLanguageListLanguages = (state: RootState): readonly LanguageListItem[] =>
    state.downloadData.project?.langs.filter(({ isDefault }) => !isDefault).map(prepareLanguage) ?? [];

export const getProjectLanguages = (state: RootState): readonly LanguageListItem[] =>
    state.downloadData.project?.langs.map(prepareLanguage) ?? [];

export const getPluralFormatSelect = (state: RootState) => state.downloadData.pluralFormatSelect ?? [];

export const getPluralFormatWhitelist = (state: RootState) => state.downloadData.pluralFormatWhitelist;

export const getPluralFormatDefaults = (state: RootState) => state.downloadData.pluralFormatDefaults;

export const getPlaceholderFormatSelect = (state: RootState) => state.downloadData.placeholderFormatSelect ?? [];

export const getPlaceholderFormatWhitelist = (state: RootState) => state.downloadData.placeholderFormatWhitelist;

export const getPlaceholderFormatDefaults = (state: RootState) => state.downloadData.placeholderFormatDefaults;

export const getIndentationSelect = (state: RootState) => state.downloadData.indentationSelect;

export const getIndentationBlacklist = (state: RootState) => state.downloadData.indentationBlacklist;

export const getIndentationDefaults = (state: RootState) => state.downloadData.indentationDefaults;

export const getTags = (state: RootState) => state.downloadData.tags ?? [];

export const getAvailableFilenames = (state: RootState) => state.downloadData?.availableFilenames ?? [];

export const getExportConfig = (state: RootState) => state.downloadData.exportConfig;

export const getCustomStatuses = (state: RootState) => {
    const projectId = state.downloadData.project?.id;

    if (!projectId) {
        return [];
    }

    return state.downloadData.customTranslationStatuses?.[projectId].statuses ?? [];
};

export const getDataRange = (state: RootState) => state.downloadData.dataRange ?? [];

export const getCustomTranslationStatusesAvailable = (state: RootState) =>
    !!state.downloadData.customTranslationStatusesAvailable;

export const getExportSortOptions = (state: RootState) => state.downloadData.exportSortOptions ?? [];

export const getExportEmptyOptions = (state: RootState) => state.downloadData.exportEmptyOptions ?? [];

export const getExportNullAsOptions = (state: RootState) => state.downloadData.exportNullAsOptions ?? [];

export const getJavaPropertiesSeparatorOptions = (state: RootState) =>
    state.downloadData.javaPropertiesSeparatorOptions ?? [];

export const getJavaPropertiesEncodingOptions = (state: RootState) =>
    state.downloadData.javaPropertiesEncodingOptions ?? [];

export const getProjectType = (state: RootState) => state.downloadData.project?.type;

export const getBaseLangIso = (state: RootState) => state.downloadData.baseLangIso;

export const getBaseLangName = (state: RootState) => state.downloadData.baseLangName;

export const getProjectName = (state: RootState) => state.downloadData.project?.name;

export const getAvailableProjects = (state: RootState) => state.downloadData.availableProjects ?? [];

export const getUnassignedKeysPerPlatformFilename = (state: RootState) =>
    state.downloadData.unassignedKeysPerPlatformFilename;

export const getEnabledTriggers = (state: RootState) => state.downloadData.enabledTriggers;

export const getRepositories = (state: RootState) => state.downloadData.repositories;

export const getProjectId = (state: RootState) => state.downloadData.project?.id;

export const getProjectBranchName = (state: RootState) => state.downloadData.projectBranchName;

export const getFileFormatMapping = (state: RootState) => state.downloadData.fileFormatMapping;

export const getIntegrationsConfig = (state: RootState) => state.downloadData.integrationsConfig;

export const getIntegrations = (state: RootState) => state.downloadData.integrations;

export const getQaTotalCount = (state: RootState) => state.downloadData.qaIssues?.total;

export const getQaIssues = (state: RootState) => state.downloadData.qaIssues?.issues;
